import type { IRecording } from '../types/Recording';
import {
  DEFAULT_STT_PRESET,
  DEFAULT_STT_PROVIDER,
  isSttPresetId,
  isSttProviderId,
} from '../types/stt';
import { queueTranscription, type TranscriptionJobHandlers } from '../services/transcriptionService';
import transcriptionJobsStore from '../stores/TranscriptionJobsStore';
import { isExcludedFromTranscription } from './transcriptionEligibility';

/** Derive the denormalised flag from raw timing data (for Firestore writes). */
export function deriveHasWordLevelTranscriptFromTimingData(timingData: unknown): boolean {
  return Array.isArray(timingData) && timingData.length > 0;
}

/** True when the recording has word-level timing data from speech-to-text. */
export function hasWordLevelTranscript(
  recording: Pick<IRecording, 'timingData' | 'hasWordLevelTranscript'>
): boolean {
  if (recording.hasWordLevelTranscript === true) {
    return true;
  }
  if (recording.hasWordLevelTranscript === false) {
    return false;
  }
  return deriveHasWordLevelTranscriptFromTimingData(recording.timingData);
}

export function getRecordingAudioPath(recording: Pick<IRecording, 'file'>): string {
  return recording.file?.recordingPath?.trim() || '';
}

export function canTranscribeRecording(
  recording: Pick<IRecording, 'file' | 'tags' | 'transcriptionExcludedReason'>
): boolean {
  const path = getRecordingAudioPath(recording);
  if (!path || path.startsWith('demo://')) {
    return false;
  }

  return !isExcludedFromTranscription(recording);
}

export function queueRecordingTranscription(
  recording: IRecording,
  options: {
    bypassModeration?: boolean;
    handlers?: TranscriptionJobHandlers;
  } = {}
): boolean {
  if (transcriptionJobsStore.hasJobForRecording(recording.key)) {
    return false;
  }

  const fileName = getRecordingAudioPath(recording);
  if (!canTranscribeRecording(recording)) {
    return false;
  }

  const speakerMode = recording.speakerMode === 'multiple' ? 'multiple' : 'single';
  const sttProvider = isSttProviderId(recording.sttProvider)
    ? recording.sttProvider
    : DEFAULT_STT_PROVIDER;
  const sttPreset = isSttPresetId(recording.sttPreset) ? recording.sttPreset : DEFAULT_STT_PRESET;

  queueTranscription(
    {
      fileName,
      recordingId: recording.key,
      transcriptionText: recording.transcription || '',
      timingData: recording.timingData || {},
      bypassModeration: options.bypassModeration ?? false,
      speakerMode,
      sttProvider,
      sttPreset,
    },
    options.handlers
  );

  return true;
}
